import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import classNames from 'classnames';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import StaticLayoutInner from '@/layouts/StaticLayoutInner';
import ArrowRight from '@/images/icons/ArrowRight';
import { ROUTES } from '@/config/routes';
import LoadingSkeleton from './LoadingSkeleton';
import useSubscriptionData from './useSubscriptionData';
import SubscriptionStatus from './SubscriptionStatus';
import SubscriptionStatusOld from '../ManageSubscription/SubscriptionStatus';
import CircularProgress from '@/components/CircularProgress';
import { pluralize } from '@/utils/pluralize';
import ThreeDotsVertical from '@/images/icons/ThreeDotsVertical';
import Dropdown from '@/components/Dropdown';
import { DropdownContext } from '@/components/Dropdown/DropdownContext';
import { isActiveSubscription, isCanceledSubscription, isEndingSubscription, priceView } from '@/utils/utils';
import { useWindowWidth } from '@/hooks/useWindowWidth';
import { fetchAPI } from '@/api';
import ErrorModal from '@/components/ErrorModal';
import CancelModal from './CancelModal';
import ThanksModal from './ThanksModal';
import CouponModal from './CouponModal';

const numberFormat = (number) => {
	return new Intl.NumberFormat('en-US', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 0
	}).format(number);
};

const StatBlock = ({ title, value, limit, showLastDays = false }) => {
  return (
    <div className="flex flex-col h-full">
      <div>
        <div className="text-[14px]/[1.3] font-semibold mb-1.5">{title}</div>
        {showLastDays && (
          <div className="text-[11px]/[1.1] font-medium text-[#A5A5A5]">Last 30 days</div>
        )}
      </div>
      <div className="mt-auto">
        <div className="text-3xl/[0.9] font-founders mb-2">{value}</div>
        {limit && (
          <div>
            {limit === -1 ? '/ Unlimited' : `/ ${limit} limit`}
          </div>
        )}
      </div>
    </div>
  );
};

const MobileStatBlock = ({ title, value, limit, showLastDays = false, children }) => {
  return (
    <div className="bg-white border border-[#F2F2F2] rounded-xl py-4 md:py-4 px-4 md:px-6 flex-grow flex md:hidden flex-col items-start mb-4 md:mb-0">
      <div className="flex justify-between w-full mb-6">
        <div className="text-[13px]/[1.1] font-semibold">{title}</div>
        {showLastDays && !children && (
          <div className="text-[11px]/[1.1] text-[#A5A5A5] font-medium mt-0.5">Last 30 days</div>
        )}
      </div>
      <div className="text-2xl/[0.9] font-founders mb-2">{value}</div>
      {limit && (
        <div className="text-[14px]/[1.3]">
          {limit === -1 ? '/ Unlimited' : `/ ${limit} limit`}
        </div>
      )}
      {children}
    </div>
  );
};

const ManageSubscriptionNew = () => {
  const auth = useSelector(state => state.auth);
  const navigate = useNavigate();
  const windowWidth = useWindowWidth();
  const { isLoading, subscription, invoices, currentPlan, setSubscription, customerPortalData, projectsCount, salesVolume, accessCodesUsed, discounts } = useSubscriptionData(auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const { openDropdownId } = useContext(DropdownContext);
  const isDropdownOpen = openDropdownId === 'toggle-dropdown-billing';

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelModalMessage, setCancelModalMessage] = useState("Are you sure you want to cancel the subscription?");
  const [cancelModalIsConfirmed, setCancelModalIsConfirmed] = useState(false);

  const [thanksModalOpen, setThanksModalOpen] = useState(false);
  const [thanksModalMessage, setThanksModalMessage] = useState("Waiting for your subscription to confirm...");
  const [thanksModalError, setThanksModalError] = useState(null);

  const [couponModalOpen, setCouponModalOpen] = useState(false);

  const [errorModal, setErrorModal] = useState(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  const salesVolumePercentage = useMemo(() => {
    if (!currentPlan || !salesVolume) return 0;
    if (currentPlan.salesLimit === -1) return -1;

    return Math.min(Math.round((Math.round(salesVolume) / currentPlan.salesLimit) * 100), 100);
  }, [currentPlan, salesVolume]);

  const hasUnlimitedSales = useMemo(() => {
    return currentPlan?.salesLimit === -1;
  }, [currentPlan]);

  const onEnterCoupon = async () => {
    if (isActiveSubscription(subscription)) {
      setCouponModalOpen(true);
    }
  }

  const onToggleSubscription = async () => {
    if (isActiveSubscription(subscription)) {
      setCancelModalMessage("Are you sure you want to cancel the subscription?");
      setCancelModalOpen(true);
    }

    if (isEndingSubscription(subscription)) {
      setCancelModalMessage(null);
      setCancelModalIsConfirmed(true);
      setCancelModalOpen(true);

      try {
        const response = await fetchAPI("/billing/reactivate-subscription", auth.user.token);
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }

        const data = await response.json();

        if (data) {
          if (data.subscription) {
            setSubscription(data.subscription);
          }
          if (data.message) {
            setCancelModalMessage(data.message);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        let errorMessage = 'An unexpected error occurred';
        if (error.response?.message && error.name !== "AbortError") {
          errorMessage = error.response.message;
        }
        setErrorModal(errorMessage);
      }
    }
  }

  useEffect(() => {
    if (!isLoading && (!currentPlan || Object.keys(currentPlan).length === 0) && (!subscription || Object.keys(subscription).length === 0)) {
      navigate('/join', {
        replace: true,
      });
    }
  }, [subscription, currentPlan, isLoading]);

  useEffect(() => {
    if (typeof searchParams.get('subscribed') === 'string') {
      navigate(ROUTES.getSubscriptionRoutes().manageSubscription, {
        replace: true,
      });
      setThanksModalOpen(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (errorModal) {
      setCancelModalOpen(false);
      setThanksModalOpen(false);
      setErrorModalOpen(true);
    }
  }, [errorModal]);

  useEffect(() => {
    if (!errorModalOpen) {
      setErrorModal(null);
    }
  }, [errorModalOpen]);

  return (
    <StaticLayoutInner>
      <div className="flex flex-col w-full sm:grid grid-cols-[2fr_1fr] mb-8 sm:mb-12 relative">
        <h1 className="h1 col-start-1 col-end-2 mb-4">Plans & Billing</h1>
        <div className="text-[#A5A5A5] col-start-1 col-end-2">
          Here you can change your plan, update your payment method, and follow your billing history.
        </div>
        <div className="hidden md:flex justify-end mt-8 sm:mt-0 col-start-2 row-start-1 gap-2.5">
          {isActiveSubscription(subscription) && (
            <div className="flex justify-end mt-8 sm:mt-0 col-start-2 row-start-1">
              <Link to={ROUTES.getSubscriptionRoutes().manageSubscription_changePlan} className="btn-secondary min-w-[60px] sm:h-8 h-10 py-1.5 px-3">Change plan</Link>
            </div>
          )}
          {isCanceledSubscription(subscription) && (
            <div className="flex justify-end mt-8 sm:mt-0 col-start-2 row-start-1">
              <Link to="/join" className="btn-primary min-w-[60px] sm:h-8 h-10 py-1.5 px-3">
                Subscribe
              </Link>
            </div>
          )}
          {isActiveSubscription(subscription) && windowWidth >= 768 && (
            <Dropdown
              id="toggle-dropdown-billing"
              button={
                <button className={classNames(
                  'btn-secondary w-8 h-8 outline-0',
                  isDropdownOpen && 'bg-[#DFD8CD] border-[#DFD8CD]',
                )}>
                  <ThreeDotsVertical />
                </button>
              }
              className="relative inline-flex self-start"
              top="top-[calc(100%+10px)]"
              minWidth="min-w-52"
            >
              <button
                className="flex items-center py-2.5 px-4 w-full text-white text-sm font-medium border-b border-b-[#313334] last:border-b-0 first:rounded-t-xl last:rounded-b-xl hover:bg-[#313334] transition"
                onClick={() => onEnterCoupon()}
              >
                Enter coupon
              </button>  
              <button
                className="flex items-center py-2.5 px-4 w-full text-white text-sm font-medium border-b border-b-[#313334] last:border-b-0 first:rounded-t-xl last:rounded-b-xl hover:bg-[#313334] transition"
                onClick={() => onToggleSubscription()}
              >
                Cancel subscription
              </button>
            </Dropdown>
          )}
        </div>
      </div>
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        (currentPlan && currentPlan.available) ? (
          <>
            {subscription && Object.keys(subscription).length > 0 && (
              <div className="mt-8 md:mt-14">
                <div className={classNames(
                  'flex flex-col mb-4 md:mb-12',
                  subscription.stripeSubscription?.status === 'canceled' ? 'md:col-span-12' : 'md:col-span-7',
                )}>
                  <div className="text-sm font-semibold mb-3 hidden md:block">Current plan</div>
                  <div className="bg-white border border-[#F2F2F2] rounded-xl py-4 md:py-4 px-4 md:px-6 flex-grow flex flex-col items-start relative mb-4 md:mb-0">
                    <div className="text-[13px] font-semibold mb-5 block md:hidden">Current plan</div>
                    {isActiveSubscription(subscription) && windowWidth < 768 && (
                      <Dropdown
                        id="toggle-dropdown-billing"
                        button={
                          <button className={classNames(
                            'btn-secondary w-8 h-8 outline-0',
                            isDropdownOpen && 'bg-[#DFD8CD] border-[#DFD8CD]',
                          )}>
                            <ThreeDotsVertical />
                          </button>
                        }
                        className="absolute top-3 right-3 inline-flex self-start"
                        top="top-[calc(100%+10px)]"
                        minWidth="min-w-52"
                      >
                        <button
                          className="flex items-center py-2.5 px-4 w-full text-white text-sm font-medium border-b border-b-[#313334] last:border-b-0 first:rounded-t-xl last:rounded-b-xl hover:bg-[#313334] transition"
                          onClick={() => onEnterCoupon()}
                        >
                          Enter coupon
                        </button>  
                        <button
                          className="flex items-center py-2.5 px-4 w-full text-white text-sm font-medium border-b border-b-[#313334] last:border-b-0 first:rounded-t-xl last:rounded-b-xl hover:bg-[#313334] transition"
                          onClick={() => onToggleSubscription()}
                        >
                          Cancel subscription
                        </button>
                      </Dropdown>
                    )}
                    <div className={classNames(
                        'flex flex-col md:flex-row items-start w-full',
                        subscription.stripeSubscription?.status === 'canceled' ? 'mb-1' : 'mb-1 md:mb-10'
                    )}>
                      <div className="flex flex-row items-start">
                        <h2 className="h2 mb-0">{currentPlan?.name}</h2>

                        {(subscription.stripeSubscription?.status === 'active' || subscription.stripeSubscription?.status === 'trialing') && (
                          subscription.stripeSubscription?.cancel_at_period_end ? (
                            <span className="badge -gray bg-[#E8E8E8] mt-0 sm:mt-[5px] mx-2 shrink-0">Cancels {moment(subscription.stripeSubscription?.current_period_end * 1000).format("MMM DD")}</span>
                          ) : (
                            <span className="badge -green mt-0 sm:mt-[5px] mx-2 shrink-0">Active</span>
                          )
                        )}
                        {subscription.stripeSubscription?.status === 'canceled' && (
                          <span className="badge -gray-2 mt-0 sm:mt-[5px] mx-2 shrink-0">Canceled</span>
                        )}
                        {(subscription.stripeSubscription?.status === 'unpaid' || subscription.stripeSubscription?.status === 'past_due') && (
                          <span className="badge -red mt-0 sm:mt-[5px] mx-2 shrink-0">Past due</span>
                        )}
                      </div>
                      {discounts && discounts.filter(d => d.coupon).length > 0 && (
                          <div className="text-[14px]/[1.3] text-[#A5A5A5] font-medium mt-2">
                            {discounts.filter(d => d.coupon).map((d, idx) => (
                                <div key={`discount-${idx}`}>{d.coupon}</div>
                            ))}
                          </div>
                      )}
                      <SubscriptionStatus subscription={subscription} />
                    </div>

                    {!isCanceledSubscription(subscription) && (
                      <div className="flex-wrap mb-4 hidden md:flex">
                        <div className="mr-6 xl:mr-8">
                          <CircularProgress
                            percentage={salesVolumePercentage}
                            isUnlimited={hasUnlimitedSales}
                          />
                        </div>
                        <div className="flex flex-col pr-5 xl:pr-14 border-r border-r-[#E8E8E8]">
                          <StatBlock
                            title="Sales volume"
                            value={priceView(Math.round(salesVolume || 0), 'USD')}
                            limit={currentPlan?.salesLimit === -1 ? -1 : new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            }).format(currentPlan?.salesLimit || 0)}
                            showLastDays={true}
                          />
                        </div>
                        {currentPlan && (
                          <div className="flex flex-col px-5 xl:px-14 border-r border-r-[#E8E8E8]">
                            <StatBlock
                              title="Projects"
                              value={pluralize(projectsCount || 0, 'project')}
                              limit={`${numberFormat(currentPlan.maxProjectsCount || 0)}`}
                            />
                          </div>
                        )}
                        {currentPlan && (
                          <div className="flex flex-col pl-5 xl:px-14">
                            <StatBlock
                              title="Access codes"
                              value={pluralize(accessCodesUsed || 0, 'code')}
                              limit={currentPlan?.maxAccessCodes === -1 ? -1 : `${numberFormat(currentPlan.maxAccessCodes || 0)}`}
                              showLastDays={true}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    {isCanceledSubscription(subscription) && (
                      <Link to="/join" className="btn-primary sm:h-8 h-10 py-1.5 px-3 mt-4 mb-2 md:hidden">
                        Subscribe
                      </Link>
                    )}
                    {isEndingSubscription(subscription) && (
                      <button className="btn-secondary mt-4 mb-2" onClick={() => onToggleSubscription()}>
                        Don't cancel subscription
                      </button>
                    )}
                  </div>

                  {/* Mobile stat blocks moved outside the main container */}
                  {!isCanceledSubscription(subscription) && (
                    <div className="md:hidden">
                      <MobileStatBlock
                        title="Sales volume"
                        value={priceView(Math.round(salesVolume || 0), 'USD')}
                        limit={currentPlan?.salesLimit === -1 ? -1 : new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        }).format(currentPlan?.salesLimit || 0)}
                        showLastDays={false}
                      >
                        <div className="w-full flex flex-col items-center mt-6">
                          <CircularProgress
                            percentage={salesVolumePercentage}
                            isUnlimited={hasUnlimitedSales}
                          />
                          <div className="text-[11px]/[1.1] font-medium text-[#A5A5A5] text-center mt-3">Last 30 days</div>
                        </div>
                      </MobileStatBlock>

                      {currentPlan && (
                        <MobileStatBlock
                          title="Number of projects"
                          value={pluralize(projectsCount || 0, 'project')}
                          limit={`${numberFormat(currentPlan.maxProjectsCount || 0)}`}
                        />
                      )}

                      {currentPlan && (
                        <MobileStatBlock
                          title="Access codes"
                          value={pluralize(accessCodesUsed || 0, 'code')}
                          limit={currentPlan?.maxAccessCodes === -1 ? -1 : `${numberFormat(currentPlan.maxAccessCodes || 0)}`}
                          showLastDays={true}
                        />
                      )}
                    </div>
                  )}
                </div>
                {subscription.stripeSubscription && (
                  <div className="col-span-12 md:col-span-5 flex flex-col">
                    <div className="text-sm font-semibold mb-3 hidden md:block">Payment method</div>
                    <div className="bg-white border border-[#F2F2F2] rounded-xl py-4 md:py-4 px-4 md:px-6 flex-grow flex flex-col items-start">
                      <div className="text-[13px] font-semibold mb-6 block md:hidden">Payment method</div>
                      <div className="text-[13px] md:text-base pr-4 sm:pr-[3.5vw] mb-6">
                        You can change your payment method and download your invoices on our customer portal.
                      </div>
                      <div className="mt-8 md:mt-auto mb-2 flex flex-col flex-wrap items-start gap-2 w-full md:w-auto">
                        {customerPortalData && customerPortalData.map((button, index) => (
                          <a key={`customer-portal-button-${index}`}
                             href={button.url}
                             target="_blank"
                             rel="noopener noreferrer"
                             className="btn-secondary"
                          >
                            {button.text}
                            <ArrowRight className="w-5 h-5 ml-1 -mr-1" />
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          (subscription && Object.keys(subscription).length > 0) && (
            <div className={classNames(
              'grid grid-cols-12 mt-8 md:mt-14',
              subscription.stripeSubscription?.status !== 'canceled' && 'gap-4',
            )}>
              <div className={classNames(
                'col-span-12 flex flex-col',
                subscription.stripeSubscription?.status === 'canceled' ? 'md:col-span-12 mb-4 md:mb-12' : 'md:col-span-7',
              )}>
                <div className="text-sm font-semibold mb-3 hidden md:block">Current plan</div>
                <div className="bg-white border border-[#F2F2F2] rounded-xl py-4 md:py-4 px-4 md:px-5 flex-grow flex flex-col items-start relative">
                  <div className="text-[13px] font-semibold mb-6 block md:hidden">Current plan</div>
                  {isActiveSubscription(subscription) && windowWidth < 768 && (
                    <Dropdown
                      id="toggle-dropdown-billing"
                      button={
                        <button className={classNames(
                          'btn-secondary w-8 h-8 outline-0',
                          isDropdownOpen && 'bg-[#DFD8CD] border-[#DFD8CD]',
                        )}>
                          <ThreeDotsVertical />
                        </button>
                      }
                      className="absolute top-3 right-3 inline-flex self-start"
                      top="top-[calc(100%+10px)]"
                      minWidth="min-w-52"
                    >
                      <button
                        className="flex items-center py-2.5 px-4 w-full text-white text-sm font-medium border-b border-b-[#313334] last:border-b-0 first:rounded-t-xl last:rounded-b-xl hover:bg-[#313334] transition"
                        onClick={() => onToggleSubscription()}
                      >
                        Cancel subscription
                      </button>
                    </Dropdown>
                  )}
                  <div className={classNames(
                    'flex flex-row items-start w-full',
                    subscription.stripeSubscription?.status === 'canceled' ? 'mb-1' : 'mb-7'
                  )}>
                    <h2 className="h2 mb-0">{currentPlan?.name}</h2>
                    {(subscription.stripeSubscription?.status === 'active' || subscription.stripeSubscription?.status === 'trialing') && (
                      subscription.stripeSubscription?.cancel_at_period_end ? (
                        <span className="badge -gray bg-[#E8E8E8] mt-0 sm:mt-[5px] mx-2 shrink-0">Cancels {moment(subscription.stripeSubscription?.current_period_end * 1000).format("MMM DD")}</span>
                      ) : (
                        <span className="badge -green mt-0 sm:mt-[5px] mx-2 shrink-0">Active</span>
                      )
                    )}
                    {subscription.stripeSubscription?.status === 'canceled' && (
                      <span className="badge -gray-2 mt-0 sm:mt-[5px] mx-2 shrink-0">Canceled</span>
                    )}
                    {(subscription.stripeSubscription?.status === 'unpaid' || subscription.stripeSubscription?.status === 'past_due') && (
                      <span className="badge -red mt-0 sm:mt-[5px] mx-2 shrink-0">Past due</span>
                    )}
                    {subscription.stripeSubscription?.status !== 'canceled' && (
                      <div className="badge px-2.5 bg-[#FAF8F4] text-[#A3A8A9] mt-0 md:mt-1 ml-auto shrink-0">
                        <span className="text-brand-green mr-1">{pluralize(projectsCount || 0, 'project')}</span>
                        of {currentPlan?.maxProjectsCount || 0}
                      </div>
                    )}
                  </div>
                  {discounts && discounts.filter(d => d.coupon).length > 0 && (
                    <div className="text-[13px]/[1.2] text-[#A5A5A5] font-medium mt-1 mb-3">
                      {discounts.filter(d => d.coupon).map((d, idx) => (
                        <div key={`discount-mobile-${idx}`}>{d.coupon}</div>
                      ))}
                    </div>
                  )}
                  {subscription.stripeSubscription?.status === 'canceled' && (
                    <div className="w-full md:w-auto border-t border-t-[#E8E8E8] md:border-t-0 pt-4 md:pt-0 md:ml-auto mt-6 md:mt-2 flex flex-row gap-2.5 text-[13px]/[1.1] md:text-[14px]/[1.3] md:hidden" />
                  )}
                  {!subscription.stripeSubscription?.cancel_at_period_end && subscription.stripeSubscription?.status !== 'past_due' && subscription.stripeSubscription?.status !== 'canceled' && (
                    <div className="flex flex-wrap gap-x-10 gap-y-7 mb-8">
                      <SubscriptionStatusOld subscription={subscription} />
                      <div>
                        <div className="text-[11px] md:text-[13px]/[1.2] text-[#A5A5A5] font-medium mb-2">
                          Our transaction fee
                        </div>
                        <div className="text-sm md:text-base">{currentPlan?.applicationFeePercent || 0}%</div>
                      </div>
                      {subscription?.stripeSubscription?.created && (
                        <div>
                          <div className="text-[11px] md:text-[13px]/[1.2] text-[#A5A5A5] font-medium mb-2">Created on
                          </div>
                          <div className="text-sm md:text-base">{moment(subscription.stripeSubscription?.created * 1000).format("LL")}</div>
                        </div>
                      )}
                    </div>
                  )}
                  {invoices && invoices.length > 0 && (
                    <>
                      <div>You have open <span className={"font-semibold"}>unpaid</span> invoices:</div>
                      {invoices.map((invoice, index) => (
                        <div key={index}>
                          <span className={"font-semibold mr-1"}>${(invoice.amount_due/100).toFixed(2)}</span>
                          <span>issued</span>
                          <span className={"font-semibold ml-1"}>{moment(invoice.created * 1000).format("LL")}</span>
                          <a href={invoice.hosted_invoice_url} target={"_blank"} rel="noopener noreferrer" className="btn-primary sm:h-8 h-10 py-1.5 px-3 mt-auto mb-2 ml-2">Pay</a>
                        </div>
                      ))}
                    </>
                  )}
                  {isActiveSubscription(subscription) && (
                    <div className="w-full p-4 md:p-5 bg-[#FFFEDD] border border-[#EAE9C2] rounded-[10px] md:rounded-[8px] text-[#9C8E41] text-[13px]/[1.2] md:text-sm/[1.3] font-medium">
                      <div className="flex flex-col md:flex-row gap-4 md:gap-3 mb-5 md:mb-6">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 translate-y-0.5">
                          <rect width="20" height="20" rx="10" fill="currentColor"/>
                          <path d="M10 4.79999V11.2" stroke="white" strokeWidth="1.7" strokeMiterlimit="10" strokeLinecap="round"/>
                          <circle cx="10" cy="14.5" r="1" fill="white"/>
                        </svg>
                        <div className="font-normal">
                          <div className="font-semibold pb-2">Legacy Plans Retire December 2025 — Update Now</div>
                          
                          <div>All legacy plans will be cancelled in <b>December 2025</b>.</div>
                          <div>Our new plans are <b>lower-cost for most customers</b> and include 0% transaction fees.</div>
                          {subscription.salesVolume > 5000 && (
                            <div>Switch within 30 days and receive an <b>exclusive 18-month discount</b>.</div>
                          )}
                          <div>Choose <b>yearly billing</b> at checkout to save an <b>extra 20%</b>.</div>
                        </div>
                      </div>
                      <Link to={ROUTES.getSubscriptionRoutes().manageSubscription_changePlan} className="font-semibold mb-1 xs:mb-0 hover:underline">Change plan →</Link>
                    </div>
                  )}
                  {isCanceledSubscription(subscription) && (
                    <Link to="/join" className="btn-primary sm:h-8 h-10 py-1.5 px-3 mt-4 mb-2 md:hidden">
                      Subscribe
                    </Link>
                  )}
                  {isEndingSubscription(subscription) && (
                    <button className="btn-secondary mt-4 mb-2" onClick={() => onToggleSubscription()}>
                      Don't cancel subscription
                    </button>
                  )}
                </div>
              </div>
              {subscription.stripeSubscription && (
                <div className={classNames(
                  'col-span-12 flex flex-col',
                  subscription.stripeSubscription?.status !== 'canceled' && 'md:col-span-5',
                )}>
                  <div className="text-sm font-semibold mb-3 hidden md:block">Payment method</div>
                  <div className="bg-white border border-[#F2F2F2] rounded-xl py-4 md:py-4 px-4 md:px-5 flex-grow flex flex-col items-start">
                    <div className="text-[13px] font-semibold mb-6 block md:hidden">Payment method</div>
                    <div className="text-[13px] md:text-base pr-4 sm:pr-[3.5vw] mb-4">
                      You can change your payment method and download your invoices on our customer portal.
                    </div>
                    <div className="mt-8 md:mt-auto mb-2 flex flex-col flex-wrap items-start gap-2 w-full md:w-auto">
                      {customerPortalData && customerPortalData.map((button, index) => (
                        <a key={`customer-portal-button-${index}`}
                           href={button.url}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="btn-secondary"
                        >
                          {button.text}
                          <ArrowRight className="w-5 h-5 ml-1 -mr-1" />
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        )
      )}
      <ErrorModal
        modalOpen={errorModalOpen}
        setModalOpen={setErrorModalOpen}
        error={errorModal}
      />
      <CancelModal
        modalOpen={cancelModalOpen}
        setModalOpen={setCancelModalOpen}
        modalMessage={cancelModalMessage}
        setModalMessage={setCancelModalMessage}
        setErrorModal={setErrorModal}
        isConfirmed={cancelModalIsConfirmed}
        setIsConfirmed={setCancelModalIsConfirmed}
        onSetSubscription={(subscription) => setSubscription(subscription)}
      />
      <ThanksModal
        modalOpen={thanksModalOpen}
        setModalOpen={setThanksModalOpen}
        modalMessage={thanksModalMessage}
        modalError={thanksModalError}
        setModalMessage={setThanksModalMessage}
        setModalError={setThanksModalError}
        subscription={subscription && Object.keys(subscription).length > 0}
        isRedirect={false}
      />
      <CouponModal
        modalOpen={couponModalOpen}
        setModalOpen={setCouponModalOpen}
        setErrorModal={setErrorModal}
      />
    </StaticLayoutInner>
  );
}

export default ManageSubscriptionNew;
